import { useState, useEffect, useCallback, useRef } from 'react'

import { toast } from '@/components/toasts/toast.helpers'

const getValuesFromPrompt = (prompt) => ({
  userPrompt: prompt?.user_prompt || '',
  systemPrompt: prompt?.system_prompt || '',
  model: prompt?.llm_model_id || null,
  side: prompt?.side || 'both',
  firstMinutes: prompt?.first_x_minutes || null,
  lastMinutes: prompt?.last_x_minutes || null,
  includeTimestamps: prompt?.include_timestamps || false,
  silenceThresholdSeconds: prompt?.silence_threshold_seconds || null,
})

const toApiFormat = (values) => ({
  user_prompt: values.userPrompt,
  system_prompt: values.systemPrompt,
  llm_model_id: values.model,
  side: values.side,
  first_x_minutes: values.firstMinutes || null,
  last_x_minutes: values.lastMinutes || null,
  include_timestamps: values.includeTimestamps,
  silence_threshold_seconds: values.silenceThresholdSeconds || null,
})

/**
 * Hook to manage prompt editing state and staged changes
 */
export const usePromptEditing = (prompt, task) => {
  const defaultValues = getValuesFromPrompt(prompt)

  const [editedValues, setEditedValues] = useState(defaultValues)
  const [isEditing, setIsEditing] = useState(false)
  const [stagedChanges, setStagedChanges] = useState(null)

  // Track the prompt identity we last initialized from, plus whether the user has
  // started editing locally. We re-sync `editedValues` from the prompt when we
  // switch to a different prompt, OR when the same prompt is refetched and the
  // user hasn't edited yet (so an in-flight reload picks up fresh values). Once
  // the user has made a local edit, we stop syncing so a refetch (e.g. while the
  // org/models are still loading) can't clobber the AI Model dropdown selection.
  const initializedPromptId = useRef(prompt?.id ?? null)
  const hasLocalEdits = useRef(false)

  useEffect(() => {
    if (!prompt) {
      return
    }
    const isDifferentPrompt = prompt.id !== initializedPromptId.current
    if (isDifferentPrompt || !hasLocalEdits.current) {
      initializedPromptId.current = prompt.id
      if (isDifferentPrompt) {
        hasLocalEdits.current = false
      }
      setEditedValues(getValuesFromPrompt(prompt))
    }
  }, [prompt])

  // Clear staged changes when task changes so edits don't carry over
  useEffect(() => {
    setStagedChanges(null)
  }, [task?.id])

  const setEditedValue = useCallback((field, value) => {
    hasLocalEdits.current = true
    setEditedValues((prev) => ({ ...prev, [field]: value }))
  }, [])

  // Get current values (staged changes take priority)
  const getCurrentPromptValues = useCallback(() => {
    return stagedChanges || toApiFormat(editedValues)
  }, [stagedChanges, editedValues])

  const hasChanges =
    prompt &&
    (editedValues.userPrompt !== (prompt.user_prompt || '') ||
      editedValues.systemPrompt !== (prompt.system_prompt || '') ||
      editedValues.model !== (prompt.llm_model_id || null) ||
      editedValues.side !== (prompt.side || 'both') ||
      editedValues.firstMinutes !== (prompt.first_x_minutes || null) ||
      editedValues.lastMinutes !== (prompt.last_x_minutes || null) ||
      editedValues.includeTimestamps !== (prompt.include_timestamps || false) ||
      editedValues.silenceThresholdSeconds !== (prompt.silence_threshold_seconds || null))

  const handleStageChanges = useCallback(() => {
    // Staged changes are a local edit we must protect from refetch re-syncs.
    hasLocalEdits.current = true
    setStagedChanges(toApiFormat(editedValues))
    setIsEditing(false)
    toast.success('Changes staged. Test your edits, then resolve to save.')
  }, [editedValues])

  const handleCancelEdit = useCallback(() => {
    // Reset to staged values if available, otherwise original prompt. Without
    // staged changes we're back to the prompt's values, so refetches may re-sync.
    hasLocalEdits.current = !!stagedChanges
    setEditedValues(
      stagedChanges ? getValuesFromPrompt(stagedChanges) : getValuesFromPrompt(prompt)
    )
    setIsEditing(false)
  }, [stagedChanges, prompt])

  const stagePrompt = useCallback(
    (overrides) => {
      hasLocalEdits.current = true
      const values = { ...getValuesFromPrompt(prompt), ...overrides }
      setEditedValues(values)
      setStagedChanges(toApiFormat(values))
      setIsEditing(false)
    },
    [prompt]
  )

  const handleClearStagedChanges = useCallback(() => {
    hasLocalEdits.current = false
    setStagedChanges(null)
    setEditedValues(getValuesFromPrompt(prompt))
    toast.success('Staged changes cleared')
  }, [prompt])

  return {
    editedValues,
    setEditedValue,
    isEditing,
    setIsEditing,
    stagedChanges,
    hasChanges,
    getCurrentPromptValues,
    handleStageChanges,
    handleCancelEdit,
    handleClearStagedChanges,
    stagePrompt,
  }
}
